import {
  CHEMISTRY_REACTION_ROLE_DISPLAY_NAME_TO_ENUM,
  ChemistryReactionRole,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {
  StoichiometryStructuredTableMetadata,
  StoichiometryStructuredTableRowMetadata,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomEval, CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {
  coerceToPrimitiveNumber,
  createFloatTypedValue,
  formatTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {EMPTY_TYPED_VALUE} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ResolveQueryFn} from 'notebook/tables/typed-values/TypedValueResolution';
import {getEnumValue} from 'util/enumIncludes';
import {Optional} from 'util/Types';

type YieldCellDependencies = [
  reactionRole: TypedValue | null,
  molesActual: TypedValue | null,
  molesPlan: TypedValue | null,
];

const yieldCustomEval: CustomEval<{
  tableMetadata: StoichiometryStructuredTableMetadata;
  rowMetadata: StoichiometryStructuredTableRowMetadata;
}> = (
  typedValue: TypedValue,
  columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult => {
  const [reactionRoleTypedValue, molesActual, molesPlan] = cellDependencies as YieldCellDependencies;

  const reactionRole = reactionRoleTypedValue
    ? getEnumValue(
        ChemistryReactionRole,
        CHEMISTRY_REACTION_ROLE_DISPLAY_NAME_TO_ENUM[formatTypedValue(reactionRoleTypedValue)] ?? ''
      )
    : undefined;

  if (reactionRole !== ChemistryReactionRole.PRODUCT) {
    return {val: EMPTY_TYPED_VALUE, error: null, locked: false};
  }

  const molesActualValue = coerceToPrimitiveNumber(molesActual);
  const molesPlanValue = coerceToPrimitiveNumber(molesPlan);

  if (molesActualValue == null || molesPlanValue == null) {
    return {val: createFloatTypedValue(null), error: null, locked: true};
  }

  if (molesPlanValue === 0) {
    return {
      val: createFloatTypedValue(null),
      error: {type: 'ERROR', message: 'Moles (plan) cannot be zero'},
      locked: true,
    };
  }

  const yieldValue = (molesActualValue / molesPlanValue) * 100;
  return {
    val: createFloatTypedValue(yieldValue),
    error: null,
    locked: true,
  };
};

export default yieldCustomEval;
